import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import useReveal from "../Components/useReveal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faLocationDot,
  faClock,
  faPaperPlane,
  faCalendarCheck,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../Styles/ContactPage.css";
import { CONTACT, SOCIALS } from "../Scripts/siteData";

const initialForm = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

function Contact() {
  useReveal();
  const [form, setForm] = useState(initialForm);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success(
        "Thank you! We received your message and will reach out shortly.",
        { position: "top-center" }
      );
      setForm(initialForm);
    } catch {
      toast.error("Something went wrong. Please try again or call us.", {
        position: "top-center",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-page">
      <Navbar />
      <ToastContainer />

      <section className="page-banner">
        <div className="hr-container">
          <span className="hr-eyebrow">Contact</span>
          <h1>Reach Out to Our Team</h1>
          <p>
            Connect with Harmony Rise Homecare for personalized support. We are
            here to answer your questions and help you take the first step.
          </p>
          <div className="page-crumb">
            <Link to="/">Home</Link> / Contact
          </div>
        </div>
      </section>

      <section className="hr-section">
        <div className="contact-grid hr-container">
          {/* Info */}
          <div className="contact-info reveal">
            <h2>Reach Out to Harmony Rise Homecare Team</h2>
            <p>
              Reach out today for a free quote or to learn more about how we can
              support your family with compassionate in-home care.
            </p>

            <div className="contact-item">
              <div className="contact-item-icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <div>
                <span>Phone</span>
                <a href={CONTACT.phoneHref}>{CONTACT.phone}</a>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-item-icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <div>
                <span>Email</span>
                <a href={CONTACT.emailHref} className="contact-email">
                  {CONTACT.email}
                </a>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-item-icon">
                <FontAwesomeIcon icon={faLocationDot} />
              </div>
              <div>
                <span>Address</span>
                <a
                  href={CONTACT.addressMap}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {CONTACT.address}
                </a>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-item-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div>
                <span>Availability</span>
                <strong>{CONTACT.hours}</strong>
              </div>
            </div>

            <p className="contact-socials-label">Follow Us</p>
            <div className="contact-socials">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                >
                  <FontAwesomeIcon icon={s.icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="contact-form-wrap reveal">
            <h2>Send Us a Message</h2>
            <p>Fill out the form and our care team will get back to you.</p>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="name">Full Name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="subject">Subject</label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="How can we help?"
                  required
                />
              </div>

              <div className="form-field">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about your care needs..."
                  required
                />
              </div>

              <button
                type="submit"
                className="hr-btn hr-btn-primary"
                disabled={sending}
              >
                <FontAwesomeIcon icon={faPaperPlane} />{" "}
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>

        {/* Map */}
        <div className="hr-container">
          <div className="contact-map reveal">
            <iframe
              title="Harmony Rise Homecare location"
              src="https://www.google.com/maps?q=11413+172nd+St+Ct+E+Puyallup+WA+98374&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hr-section" style={{ paddingTop: 0 }}>
        <div className="hr-container">
          <div className="cta-band reveal">
            <h2>Ready to Care?</h2>
            <p>Connect with us for personalized support tailored to you.</p>
            <div className="cta-actions">
              <Link
                to="/book-appointment"
                className="hr-btn hr-btn-white hr-btn-lg"
              >
                <FontAwesomeIcon icon={faCalendarCheck} /> Get Started
              </Link>
              <a href={CONTACT.phoneHref} className="hr-btn hr-btn-ghost hr-btn-lg">
                <FontAwesomeIcon icon={faPhone} /> Free Consultation:{" "}
                {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Contact;
